// src/components/Chat/CitationInfoPopup/CitationInfoPopup.js
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import MPSBarLoader from "../../../components/MPSBarLoader"; // Adjust path
import libraryService from "../../../services/contentLibraryService"; // Your existing service
import ArticlePreviewWrapper from "../../../Shared/ContentLibrary/Preview/ArticlePreviewWrapper"; // Adjust path
import { ROLE_PATIENT } from "../../../security/roles"; // Adjust path

const CitationInfoPopup = ({ isOpen, toggle, citationTitle, article }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [articleData, setArticleData] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setLoading(true); // Assume loading initially when opening
      setError(null); // Clear previous errors
      setArticleData(null); // Clear previous article data

      if (article && Object.keys(article).length > 0) {
        // If a pre-fetched article object is passed
        // console.log("Using pre-fetched article in popup:", article);
        setArticleData(article);
        setLoading(false);
      } else if (citationTitle) {
        // Fallback to fetching by title if no article object
        // console.log("Fetching article in popup by title:", citationTitle);
        const fetchDataByTitle = async () => {
          try {
            const result = await libraryService.loadArticle(citationTitle);
            setArticleData(result);
          } catch (err) {
            console.error("Error fetching article in popup for citation:", err);
            if (err.response && err.response.status === 404) {
              setError(`No specific information found for "${citationTitle}".`);
            } else {
              setError("Could not load information. Please try again.");
            }
            setArticleData(null);
          } finally {
            setLoading(false);
          }
        };
        fetchDataByTitle();
      } else {
        // No article object and no title to fetch
        setError("No information available to display.");
        setArticleData(null);
        setLoading(false);
      }
    } else if (!isOpen) {
      // Reset state when modal is closed
      setArticleData(null);
      setError(null);
      setLoading(false); // Ensure loading is false when closed
    }
  }, [isOpen, citationTitle, article]); // Dependencies for the effect

  const renderPopupContent = () => {
    if (loading) {
      return (
        <div
          className="text-center p-5 d-flex align-items-center justify-content-center"
          style={{ minHeight: "200px", flexGrow: 1 }} // flexGrow to take available space
        >
          <MPSBarLoader />
        </div>
      );
    }
    if (error) {
      return <p className="text-danger text-center p-3 flex-grow-1">{error}</p>;
    }
    if (articleData) {
      return (
        <div
          style={{ height: "100%", width: "100%", overflowY: "auto" }} // This div will scroll
          className="article-preview-popup-container"
        >
          <ArticlePreviewWrapper
            article={articleData}
            // Assuming ArticlePreviewWrapper can adapt to height or you style it internally
            // height="100%" // May or may not be needed depending on APW's internal styling
            userRole={ROLE_PATIENT} // Pass necessary roles or other context
            embed={true} // Important prop you mentioned
          />
        </div>
      );
    }
    // Fallback if no data, no error, and not loading (e.g., something unexpected)
    return (
      <p className="text-center p-3 flex-grow-1">No content to display.</p>
    );
  };

  // Determine the title for the modal header
  // Prioritize title from fetched/passed articleData, then citationTitle
  const modalTitle = articleData?.title || citationTitle || "Information";

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      centered
      className="citation-info-modal"
      contentClassName="citation-info-modal-content"
      // Do not use 'scrollable' on Modal if the inner container ('article-preview-popup-container') handles scrolling
    >
      <ModalHeader toggle={toggle} className="citation-info-modal-header">
        {modalTitle}
      </ModalHeader>
      <ModalBody
        className="citation-info-modal-body p-0" // p-0 because container div will handle padding/layout
        style={{
          minHeight: "70vh",
          maxHeight: "80vh",
          display: "flex", // Essential for the child with height: 100% to work
          flexDirection: "column", // Ensures child takes up vertical space
        }}
      >
        {renderPopupContent()}
      </ModalBody>
    </Modal>
  );
};

CitationInfoPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  citationTitle: PropTypes.string, // Fallback identifier if 'article' prop is not provided
  article: PropTypes.object, // Pre-fetched article data (preferred)
};

export default CitationInfoPopup;
